import { BASE_METHOD, fetchApi, FetchApiOrigin } from "~/common/API/baseApi";

export interface ICeo {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  country?: string;
  phone?: string;
  birthDate?: string;
  passport?: {
    serial?: string;
    number?: string;
    date?: string;
    division?: string;
    birthPlace?: string;
  };
}

export interface IPaymentAddress {
  type: string;
  zip: string;
  country: string;
  city: string;
  street: string;
}

export interface IBankAccount {
  account: string;
  korAccount: string;
  bankName: string;
  bik: string;
  kbk?: string;
  oktmo?: string;
  details?: string;
}

export interface IPaymentDetails {
  role: number;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  country?: string;
  phoneNumber?: string;
  phone?: string;
  inn?: string;
  isPublisher?: boolean;
  vat?: number;
  userId?: number;
  ogrn?: string;
  kpp?: string;
  ceo?: ICeo;
  type?: number;
  fullName?: string;
  name?: string;
  // Адрес для ОРД: у физлица — объект type:"actual", у ИП/юрлица — массив type:"legal"
  addresses?: IPaymentAddress | IPaymentAddress[];
}

export interface ILegalPaymentDetails {
  billingDescriptor: string;
  fullName: string;
  name: string;
  inn: string;
  kpp?: string;
  ogrn: string;
  addresses: IPaymentAddress[];
  phone: string;
  ceo: ICeo;
  bankAccount: IBankAccount;
  id?: number;
  userId?: number;
  role: number;
  paymentSystemId: number;
  type: number;
  isPublisher: boolean;
  vat: number;
}

export default class PaymentService {
  static createPaymentDetails = async (body: IPaymentDetails) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/payments/details/create/private",
      method: BASE_METHOD.POST,
      isFullScreenLoading: false,
      body,
    });
    return response;
  };

  static getWallet = async () => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/wallet/advertiser",
      method: BASE_METHOD.GET,
    });

    if (!response || response === "") {
      return {
        details: null,
      };
    }

    if (typeof response === "object" && response !== null) {
      const wallet = response as Record<string, any>;
      if (!wallet.details) {
        return {
          ...wallet,
          details: null,
        };
      }
    }

    return response;
  };

  static createWalletAdvertiser = async (body: IPaymentDetails) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/payments/details/individual/save",
      method: BASE_METHOD.POST,
      isFullScreenLoading: false,
      body,
    });
    return response;
  };

  static createWalletAdvertiserLegal = async (body: ILegalPaymentDetails | IPaymentDetails) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/payments/details/legal/save",
      method: BASE_METHOD.POST,
      isFullScreenLoading: false,
      body,
    });
    return response;
  };

  // Реквизиты конечного рекламодателя при посредническом договоре.
  // Тип реквизитов: Физ.лицо/СЗ → individual, ИП/Юр.лицо → legal.
  // Возвращают объект с id, который используется как customerRequisitesId в контракте.
  static saveIntermediaryAdvertiserRequisites = async (
    body: ILegalPaymentDetails | IPaymentDetails
  ) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/intermediary/requisites/legal/save",
      method: BASE_METHOD.POST,
      isFullScreenLoading: false,
      body,
    });
    return response;
  };

  static saveIntermediaryAdvertiserRequisitesIndividual = async (body: any) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/intermediary/requisites/individual/save",
      method: BASE_METHOD.POST,
      isFullScreenLoading: false,
      body,
    });
    return response;
  };

  // Контракт посредника на основе ID реквизитов конечного рекламодателя.
  // Возвращает объект с id → передаётся как intermediaryContractId в публикацию.
  static saveIntermediaryContract = async (body: {
    customerRequisitesId: number;
    contractType: "service" | "intermediary";
    contractDate: string;
    contractNumber?: string;
    additionalAgreementNumber?: string;
    additionalAgreementDate?: string;
  }) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/intermediary/contract/save",
      method: BASE_METHOD.POST,
      isFullScreenLoading: false,
      body,
    });
    return response;
  };

  // Список посреднических договоров пользователя. Ответ: { records: [...] }.
  // Нужен, чтобы отследить integrationStatus договора (pending → success) перед
  // созданием заказа — иначе /advert/order/create-with-publications вернёт 422.
  static getIntermediaryContracts = async () => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/intermediary/contracts",
      method: BASE_METHOD.GET,
      isFullScreenLoading: false,
    });
    return response;
  };

  // Реквизиты конечных рекламодателей пользователя. Ответ: { records: [...] }.
  // Используется, чтобы подтянуть данные ранее созданного договора
  // (по customerRequisitesId) в форму реквизитов.
  static getIntermediaryRequisites = async () => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/intermediary/requisites",
      method: BASE_METHOD.GET,
      isFullScreenLoading: false,
    });
    return response;
  };

  // Редактирование реквизитов ИП/Юр.лица: перезаписывает поля beneficiary.
  static updateWalletAdvertiserLegalBeneficiary = async (
    body: ILegalPaymentDetails | IPaymentDetails
  ) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/user/payments/details/legal/beneficiary",
      method: BASE_METHOD.POST,
      isFullScreenLoading: false,
      body,
    });
    return response;
  };

  // История счетов на пополнение баланса (вкладка «Счета и пополнение»).
  // Ответ: { records: [{ id, amount, url, status, createdAt }], meta: { pageCount } }
  // status: awaiting_payment — выставлен, paid — оплачен.
  // period: all | month | quarter | year.
  static getNominalAccountInvoiceHistory = async (
    pageNumber: number,
    period: string = "all"
  ) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/finance/acquiring/nominal-account/invoice-history",
      method: BASE_METHOD.GET,
      params: { pageNumber, period },
    });
    return response;
  };

  // Операции по кошельку (вкладка «Операции»).
  // Ответ: { records: [{ amount, type, createdAt, entityId, comment, paymentMethod,
  //                       balance, withdrawalAvailable }], meta: { pageCount } }
  // «Баланс после»: рекламодатель — balance, паблишер — withdrawalAvailable.
  // period: all | month | quarter | year (квартал — текущий календарный).
  // type: 0 — Поступление, 1 — Списание, 2 — Вывод. Без значения — все типы.
  static getWalletOperations = async (
    pageNumber: number,
    userRole: string,
    userId: number | string,
    period: string = "all",
    type?: number,
    source?: string
  ) => {
    const params: Record<string, any> = { pageNumber, userRole, userId, period };
    if (type !== undefined) params.type = type;
    if (source) params.source = source;
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/finance/wallet/operation",
      method: BASE_METHOD.GET,
      params,
    });
    return response;
  };

  // История выводов с номинального счёта (источник вкладки «Операции»).
  // Ответ: { records: [{ amount, status, createdAt, account }], meta: { pageCount } }
  // period передаём для единого фильтра ленты операций.
  static getNominalAccountWithdrawalHistory = async (
    pageNumber: number,
    period: string = "all"
  ) => {
    const response = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: "/finance/acquiring/nominal-account/withdrawal-history",
      method: BASE_METHOD.GET,
      params: { pageNumber, period },
    });
    return response;
  };
}
